/**
 * Photography scheduling arithmetic.
 *
 * The server is the authority on availability — it holds the lock that stops
 * two doctors taking the same window. This module is the same arithmetic
 * expressed as pure functions so it can be tested without a database, and so
 * the screens can explain a crew's day ("a 2-hour shoot occupies 4 hours") from
 * the same rules the server enforces.
 *
 * The whole thing turns on one idea: a booking does not occupy its own hours, it
 * occupies its hours *plus a gap at each end* — and the size of that gap depends
 * on whether the crew has to travel. Two shoots in one building need a short
 * reset; two across town need the full travel time. Everything else follows.
 */

/** Statuses that hold a slot. A cancelled shoot frees its window. */
export const LIVE_STATUSES = ['pending', 'confirmed'];

/** JS getDay() is Sun=0…Sat=6; crews store lowercase English names. */
export const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const dayKeyOf = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(date.getTime()) ? '' : DAY_KEYS[date.getDay()];
};

export function toMinutes(time) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(time || ''));
  if (!match) return NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return NaN;
  return hours * 60 + minutes;
}

export function toTime(minutes) {
  const safe = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

/**
 * Two addresses count as the same place when their text matches once
 * whitespace and case are normalised.
 *
 * Deliberately simple: this decides whether a crew has to travel, and a doctor
 * typing the same clinic name twice means the same clinic. It is not a geocoder
 * and does not pretend to be — anything it cannot match confidently falls back
 * to the longer travel gap, which is the safe direction to be wrong in.
 */
export function samePlace(a, b) {
  const norm = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const left = norm(a);
  const right = norm(b);
  return left.length > 0 && left === right;
}

/**
 * The stretch of the day an existing booking blocks, as seen from a candidate
 * shoot at `candidateLocation`.
 *
 * The gap is not a property of the booking alone — it depends on where the crew
 * goes next. Two shoots in one building need the short reset; two across town
 * need the full travel time. The wider of the two sides' settings wins, so
 * neither can be squeezed by the other's optimism.
 *
 * Clamped at zero: an early shoot with a long gap would otherwise produce a
 * negative start, which compares wrongly against another window.
 */
export function blockedWindow(booking, candidateLocation, teamGaps = {}) {
  const start = toMinutes(booking.startTime);
  if (Number.isNaN(start)) return null;

  const duration = (Number(booking.durationHours) || 0) * 60;
  const together = samePlace(booking.location, candidateLocation);

  const bookingGap = together
    ? Number(booking.sameLocationBufferMinutes) || 0
    : Number(booking.bufferMinutes) || 0;
  const teamGap = together
    ? Number(teamGaps.sameLocation) || 0
    : Number(teamGaps.travel) || 0;

  const gap = Math.max(bookingGap, teamGap);

  return { from: Math.max(0, start - gap), to: start + duration + gap, gap, together };
}

/** Half-open overlap: a window ending exactly when another starts is fine. */
export const overlaps = (a, b) => Boolean(a && b) && a.from < b.to && a.to > b.from;

/**
 * Start times a shoot of `hours` could begin at on one day.
 *
 * Mirrors the server's `/photography/availability`. Half-hour granularity —
 * finer than that offers a crew a 10:10 start, which nobody books.
 *
 * @returns {{slots: string[], reason?: string}}
 */
export function availableStarts({
  team,
  bookings = [],
  dateKey,
  hours = 1,
  location = '',
  stepMinutes = 30,
}) {
  if (!team) return { slots: [], reason: 'لم يتم اختيار فريق' };

  if (!(team.workingDays || []).includes(dayKeyOf(dateKey))) {
    return { slots: [], reason: 'الفريق لا يعمل هذا اليوم' };
  }

  const min = Number(team.minHours) || 1;
  const max = Number(team.maxHours) || 6;
  if (hours < min || hours > max) {
    return { slots: [], reason: `المدة المسموحة من ${min} إلى ${max} ساعة` };
  }

  const open = toMinutes(team.startTime);
  const close = toMinutes(team.endTime);
  if (Number.isNaN(open) || Number.isNaN(close) || close <= open) {
    return { slots: [], reason: 'ساعات عمل الفريق غير صحيحة' };
  }

  const gaps = {
    travel: Number(team.bufferMinutes) || 0,
    sameLocation: Number(team.sameLocationBufferMinutes) || 0,
  };

  const busy = bookings
    .filter((b) => b.date === dateKey && LIVE_STATUSES.includes(b.status))
    .map((booking) => blockedWindow(booking, location, gaps))
    .filter(Boolean);

  const length = hours * 60;
  const slots = [];

  // The candidate's own edge uses the widest gap any neighbour could demand of
  // it; each neighbour then applies its own on top.
  const ownGap = busy.length > 0 ? Math.max(...busy.map((w) => w.gap)) : 0;

  for (let start = open; start + length <= close; start += stepMinutes) {
    const candidate = { from: Math.max(0, start - ownGap), to: start + length + ownGap };
    if (!busy.some((window) => overlaps(candidate, window))) slots.push(toTime(start));
  }

  return { slots, gaps, effectiveGap: ownGap };
}

/**
 * What the gap actually works out to on one day, given what is already booked.
 *
 * The screens use this to say "today the gap is 15 minutes because the other
 * shoot is at the same address" rather than quoting the team's travel time and
 * leaving the doctor to wonder why more slots appeared.
 */
export function effectiveGapFor({ team, bookings = [], dateKey, location = '' }) {
  const gaps = {
    travel: Number(team?.bufferMinutes) || 0,
    sameLocation: Number(team?.sameLocationBufferMinutes) || 0,
  };

  const sameDay = bookings.filter(
    (b) => b.date === dateKey && LIVE_STATUSES.includes(b.status),
  );
  if (sameDay.length === 0) return { minutes: 0, reason: 'no-bookings', gaps };

  const windows = sameDay
    .map((booking) => blockedWindow(booking, location, gaps))
    .filter(Boolean);

  const minutes = Math.max(...windows.map((w) => w.gap));
  const allTogether = windows.every((w) => w.together);

  return {
    minutes,
    reason: allTogether ? 'same-location' : 'different-location',
    gaps,
    bookings: sameDay.length,
  };
}

/**
 * How much of a crew's day one shoot really costs.
 *
 * This is the number worth showing an admin before they set a buffer: at 60
 * minutes either side, a two-hour shoot consumes four hours, which caps most
 * crews at two jobs a day.
 */
export function occupiedHours({ hours, bufferMinutes }) {
  return (Number(hours) || 0) + ((Number(bufferMinutes) || 0) * 2) / 60;
}

/** How many shoots of `hours` fit in a crew's working day. */
export function shootsPerDay({ team, hours }) {
  const open = toMinutes(team?.startTime);
  const close = toMinutes(team?.endTime);
  if (Number.isNaN(open) || Number.isNaN(close) || close <= open) return 0;

  const length = (Number(hours) || 0) * 60;
  if (length <= 0) return 0;

  const buffer = Number(team?.bufferMinutes) || 0;
  const day = close - open;

  // The first shoot needs no leading buffer inside the working day — the crew
  // travels in before it opens — so the day fits one more than a naive
  // division by (length + 2 × buffer) suggests.
  let count = 0;
  let cursor = 0;
  while (cursor + length <= day) {
    count += 1;
    cursor += length + buffer * 2;
  }
  return count;
}

export default {
  availableStarts,
  blockedWindow,
  effectiveGapFor,
  occupiedHours,
  overlaps,
  samePlace,
  shootsPerDay,
};
